import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/shared/lib/supabase'
import type { USStatus } from '@/shared/types'

export interface UserStoryWithFeature {
  id: string
  short_id: string
  feature_id: string
  title: string
  as_a?: string
  i_want?: string
  so_that?: string
  business_rules?: string
  acceptance_criteria?: string
  test_cases?: string
  should_do?: string
  should_not_do?: string
  why?: string
  status: USStatus
  order_index: number
  created_at: string
  updated_at: string
  feature: {
    id: string
    short_id: string
    name: string
    project: {
      id: string
      short_id: string
      name: string
      test_environment_url?: string
      team: {
        id: string
        short_id: string
        name: string
      }
    }
  }
}

async function fetchUserStoryByShortId(shortId: string): Promise<UserStoryWithFeature | null> {
  const { data, error } = await supabase
    .from('user_stories')
    .select(`
      *,
      feature:features (
        id,
        short_id,
        name,
        project:projects (
          id,
          short_id,
          name,
          test_environment_url,
          team:teams (
            id,
            short_id,
            name
          )
        )
      )
    `)
    .eq('short_id', shortId)
    .single()

  if (error) {
    if (error.code === 'PGRST116') {
      return null
    }
    throw new Error(error.message)
  }

  return data as UserStoryWithFeature
}

export function useUserStoryByShortId(shortId: string | undefined) {
  return useQuery({
    queryKey: ['userStory', 'short', shortId],
    queryFn: () => fetchUserStoryByShortId(shortId!),
    enabled: !!shortId,
  })
}
